import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useEffect, useRef, useState } from 'react'
import {
  ANNOTATION_PAGE_SIZE,
  type AnnotationListSignature,
  annotationListSignature,
  isSameAnnotationList,
  loadAnnotationPages,
  MAX_ANNOTATION_PAGES,
} from './annotation-paging'

type PagedRow = { annotationId: string }

/**
 * How often a progressive load adds what it has so far, at most. Pages arrive
 * several a second on a fast connection, and every addition recounts every
 * chart, so they are gathered up and added in steps instead.
 */
export const ANNOTATION_PROGRESS_INTERVAL_MS = 500

/** A list paged to its end, or as far as it would go. */
export type PagedAnnotationList = {
  signature: AnnotationListSignature
  /** The list ended, rather than stopping at the cap or on a failed page. */
  complete: boolean
}

/**
 * The lists paged so far, per Apollo client (`scope`) and query variables.
 *
 * Kept outside the component, so a page that is left and opened again finds
 * the cached list already paged and loads nothing, instead of asking the
 * backend whether the list it already holds has ended. Keyed by client so two
 * clients (a test per mock provider, say) never share a record.
 */
const pagedLists = new WeakMap<object, Map<string, PagedAnnotationList>>()

const recordFor = (scope: object, key: string) =>
  pagedLists.get(scope)?.get(key)

const record = (scope: object, key: string, list: PagedAnnotationList) => {
  const lists = pagedLists.get(scope) ?? new Map<string, PagedAnnotationList>()
  lists.set(key, list)
  pagedLists.set(scope, lists)
}

/**
 * Whether `rows` is a list this module paged to its very end, for the query
 * with `variablesKey` on the client `scope`.
 *
 * Lets a caller answer a narrower question from a list it already has: once
 * every note is loaded, the notes of one week are among them.
 */
export const isAnnotationListComplete = (
  scope: object,
  variablesKey: string,
  rows: readonly PagedRow[] | undefined,
): boolean => {
  const list = recordFor(scope, variablesKey)
  return Boolean(
    rows && list?.complete && isSameAnnotationList(list.signature, rows),
  )
}

export type UseRemainingAnnotationPagesOptions<Row extends PagedRow> = {
  /** The first page as the query returned it, or `undefined` before it has. */
  rows: readonly Row[] | undefined
  /** The first page is still loading. */
  loading: boolean
  /** Load the older pages at all. Off, any load in flight is dropped. */
  enabled: boolean
  /**
   * Every variable of the query but the page, as a string. A new value is a
   * new list: a load for the old one is dropped and paging starts over.
   */
  variablesKey: string
  /** The Apollo client the query runs on. See {@link isAnnotationListComplete}. */
  scope: object
  maxPages?: number
  /**
   * Add the older rows as they arrive, every
   * {@link ANNOTATION_PROGRESS_INTERVAL_MS} at most, rather than all at once
   * at the end. For a page whose figures are worth seeing while the rest
   * loads.
   */
  progressive?: boolean
  /** One older page, `ANNOTATION_PAGE_SIZE` rows from `offset`. */
  fetchPage: (offset: number) => Promise<readonly Row[]>
  /** Adds older rows to the query's result. Returns the list as it then stands. */
  appendRows: (older: Row[]) => readonly PagedRow[]
}

export type UseRemainingAnnotationPagesResult = {
  /** The older pages are still loading. */
  loadingMore: boolean
  /** Loading stopped before the list ended: the page cap, or a failed page. */
  stoppedEarly: boolean
}

/**
 * Loads every page after a `tripAnnotations` query's first one and adds them
 * to its result: in one go once they are all in, or, with `progressive`, in
 * steps as they arrive.
 *
 * Pages are fetched several at a time (see `loadAnnotationPages`), and the
 * result changes a handful of times at most, so a table or chart built on it
 * is not redrawn once per page. A failed page keeps the rows before it and
 * raises the usual error notification.
 *
 * Paging starts over when `variablesKey` changes or the first page is replaced
 * (a refetch after a save, say). That is judged from the rows rather than the
 * request: a refetch of an exactly full list returns the same count, so only
 * the ends of the list show that it moved. A list already paged (on this
 * client, under these variables) is not paged again.
 */
export const useRemainingAnnotationPages = <Row extends PagedRow>({
  rows,
  loading,
  enabled,
  variablesKey,
  scope,
  maxPages = MAX_ANNOTATION_PAGES,
  progressive = false,
  fetchPage,
  appendRows,
}: UseRemainingAnnotationPagesOptions<Row>): UseRemainingAnnotationPagesResult => {
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const [loadingMore, setLoadingMore] = useState(false)
  const [stoppedEarly, setStoppedEarly] = useState(false)

  // The load in flight, and the list as it last wrote it (or started from).
  // A ref rather than effect cleanup, so a re-render mid-load (its own
  // progress write, or a cache patch from the answered toggle handing over a
  // new array with the same rows) leaves the load running instead of
  // cancelling and restarting it.
  const inFlight = useRef<{
    variables: string
    signature: string
    cancel: () => void
  } | null>(null)

  // Read when a load runs or ends rather than listed as dependencies: a new
  // function identity is not a new list, and must not restart a load that is
  // halfway through.
  const latest = useRef({ fetchPage, appendRows, enqueueMoiaErrorNotification })
  latest.current = { fetchPage, appendRows, enqueueMoiaErrorNotification }

  useEffect(() => {
    // A load for other variables (a new date window) must not land on this
    // query's result, so it is dropped before the new first page is even in.
    if (
      inFlight.current &&
      (!enabled || inFlight.current.variables !== variablesKey)
    ) {
      inFlight.current.cancel()
    }
    if (!enabled || loading || !rows) return
    const done = recordFor(scope, variablesKey)
    if (done && isSameAnnotationList(done.signature, rows)) {
      setStoppedEarly(!done.complete)
      return
    }
    const signature = annotationListSignature(rows)
    const signatureKey = JSON.stringify(signature)
    if (inFlight.current?.signature === signatureKey) return
    inFlight.current?.cancel()

    setStoppedEarly(false)
    if (rows.length < ANNOTATION_PAGE_SIZE) {
      record(scope, variablesKey, { signature, complete: true })
      return
    }

    let cancelled = false
    const load = {
      variables: variablesKey,
      signature: signatureKey,
      cancel: () => {
        cancelled = true
        if (inFlight.current === load) inFlight.current = null
        setLoadingMore(false)
      },
    }
    inFlight.current = load
    // How many of the loaded rows are already in the query's result.
    let written = rows.length
    let lastWriteAt = Date.now()
    // Adds the rows loaded since the last write, and notes the list it leaves
    // behind, so the re-render that write causes is recognised as this load's
    // own rather than as a new list to page.
    const writeUpTo = (loaded: readonly Row[]) => {
      if (loaded.length <= written) return null
      const list = latest.current.appendRows(loaded.slice(written) as Row[])
      written = loaded.length
      lastWriteAt = Date.now()
      load.signature = JSON.stringify(annotationListSignature(list))
      return list
    }

    setLoadingMore(true)
    void loadAnnotationPages({
      loaded: rows,
      maxPages,
      isCancelled: () => cancelled,
      fetchPage: offset => latest.current.fetchPage(offset),
      onBatch: progressive
        ? loaded => {
            if (cancelled) return
            if (Date.now() - lastWriteAt < ANNOTATION_PROGRESS_INTERVAL_MS) {
              return
            }
            writeUpTo(loaded)
          }
        : undefined,
    }).then(result => {
      if (!result || cancelled) return
      inFlight.current = null
      if (result.error !== undefined) {
        latest.current.enqueueMoiaErrorNotification(result.error)
      }
      // When the last batch added nothing (an empty or failed final page), the
      // query's result is still the list the progressive writes left, which
      // is every loaded row. Recording the first page instead would mistake a
      // later refetch of those same newest rows for this list, already paged,
      // and leave the older rows out.
      const list = writeUpTo(result.rows) ?? result.rows
      record(scope, variablesKey, {
        signature: annotationListSignature(list),
        complete: result.complete,
      })
      setStoppedEarly(!result.complete)
      setLoadingMore(false)
    })
  }, [enabled, loading, rows, variablesKey, scope, maxPages, progressive])

  // Drops a load still running when the page goes away.
  useEffect(() => () => inFlight.current?.cancel(), [])

  return { loadingMore, stoppedEarly }
}
